import type { ReactNode } from 'react';

import { getStyles } from '@shieldpay/theme-provider-ui/styles';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import type { BoxProps } from '../box/box';
import { Box } from '../box/box';
import type { ValidationMessagesProps } from '../field-container/utilities';
import { ValidationMessages } from '../field-container/utilities';

import * as styles from './form.css';

type ConformFormCompoundProps = {
  children: ReactNode;
} & Pick<BoxProps, 'gridItem'>;

/**
 * For use inside <Form />, contains the main heading of the form
 */
const FormStickyHeader = ({ children, gridItem }: ConformFormCompoundProps) => (
  <Box
    className={classFilter([
      getStyles({
        gridItem,
      }),
      styles.header,
    ])}
    padding={{ y: 'basePos3' }}
    background="neutral000"
  >
    {children}
  </Box>
);

/**
 * For use inside <Form />, contains the main content of the form
 */
const FormContent = ({
  children,
  validationMessages,
}: ConformFormCompoundProps & {
  validationMessages?: ValidationMessagesProps;
}) => (
  <Box className={styles.content} spacing="base" variant="grid">
    {/* TODO is attaching the form id to the error id correct a11y? */}
    {validationMessages ? (
      <ValidationMessages
        errors={validationMessages?.errors}
        id={validationMessages?.id}
      />
    ) : null}
    {children}
  </Box>
);

/**
 * For use as final child of <Form /> to contain all action buttons
 */
const FormActions = ({ children, gridItem }: ConformFormCompoundProps) => (
  <Box
    className={classFilter([
      getStyles({
        gridItem,
      }),
      styles.actions,
    ])}
    padding={{ y: 'basePos3' }}
    background="neutral000"
  >
    {children}
  </Box>
);

export type FormProps = Pick<
  BoxProps,
  | 'children'
  | 'spacing'
  | 'padding'
  | 'title'
  | 'Component'
  | 'method'
  | 'autoComplete'
> & {
  encType?: 'multipart/form-data';
  variant?: 'default' | 'grid';
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
};

export const Form = ({
  Component = 'form',
  variant = 'default',
  spacing = variant === 'grid' ? 'basePos3' : undefined,
  ...props
}: FormProps) => {
  return (
    <Box
      variant={variant === 'grid' ? 'grid' : undefined}
      spacing={spacing}
      Component={Component}
      className={variant === 'grid' ? styles.gridForm : ''}
      {...props}
    />
  );
};

Form.StickyHeader = FormStickyHeader;
Form.Content = FormContent;
Form.Actions = FormActions;
